import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CustomerMailDomainDto,
  MailboxPreferencesDto,
  MailboxPreferencesInput,
} from '@cpm/shared';
import { api } from '../api/client.js';
import { Button, ErrorNote, Input, Label, Select, Spinner, Textarea } from './ui.js';

function PreferenceForm({ data, customer }: { data: MailboxPreferencesDto; customer: boolean }) {
  const qc = useQueryClient();
  const [value, setValue] = useState<MailboxPreferencesInput>(data.preferences);
  const refresh = () => qc.invalidateQueries({ queryKey: ['mail', 'preferences', data.mailboxId] });
  const save = useMutation({
    mutationFn: () =>
      api.put<MailboxPreferencesDto>(`/api/mail/mailboxes/${data.mailboxId}/preferences`, value),
    onSuccess: refresh,
    onError: refresh,
  });
  const setDraft = (next: MailboxPreferencesInput) => {
    setValue(next);
    save.reset();
  };
  const reconcile = useMutation({
    mutationFn: () => api.post(`/api/mail/mailboxes/${data.mailboxId}/preferences/reconcile`, {}),
    onSuccess: refresh,
  });
  if (data.state !== 'ready')
    return (
      <div className="space-y-3">
        <p className="text-sm">{data.explanation}</p>
        {data.state === 'pending' && !customer && (
          <Button loading={reconcile.isPending} onClick={() => reconcile.mutate()}>
            Reconcile preferences
          </Button>
        )}
        <ErrorNote error={reconcile.error} />
      </div>
    );
  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <p className="text-sm text-slate-500">
        Replies request a one-day interval per sender; the mail server enforces its limits. Saving a
        changed reply may reset sender tracking. Existing custom or migrated rules require provider
        review.
      </p>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.vacationEnabled}
          onChange={(e) => setDraft({ ...value, vacationEnabled: e.target.checked })}
        />
        Enable vacation replies
      </label>
      <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <legend className="px-1 text-sm font-medium">Vacation schedule</legend>
        <p className="text-sm text-slate-500">
          Times are in UTC. Leave the start empty to begin when enabled, or the end empty to
          continue until disabled. Replies start at the start time and stop at the end time. The
          mail server enforces the saved schedule.
        </p>
        <div>
          <Label htmlFor="reply-start">Start time (UTC, optional)</Label>
          <Input
            id="reply-start"
            type="datetime-local"
            step={1}
            value={value.startsAt?.slice(0, 19) ?? ''}
            onChange={(e) =>
              setDraft({
                ...value,
                startsAt: e.target.value
                  ? `${e.target.value.length === 16 ? e.target.value + ':00' : e.target.value}Z`
                  : null,
              })
            }
          />
        </div>
        <div>
          <Label htmlFor="reply-end">End time (UTC, optional)</Label>
          <Input
            id="reply-end"
            type="datetime-local"
            step={1}
            value={value.endsAt?.slice(0, 19) ?? ''}
            onChange={(e) =>
              setDraft({
                ...value,
                endsAt: e.target.value
                  ? `${e.target.value.length === 16 ? e.target.value + ':00' : e.target.value}Z`
                  : null,
              })
            }
          />
        </div>
        {value.startsAt && value.endsAt && value.endsAt <= value.startsAt && (
          <p role="alert" className="text-sm text-red-600">
            The end time must be after the start time.
          </p>
        )}
        <Button
          type="button"
          variant="secondary"
          disabled={!value.startsAt && !value.endsAt}
          onClick={() => setDraft({ ...value, startsAt: null, endsAt: null })}
        >
          Clear schedule
        </Button>
        <p className="text-sm text-slate-500">
          A schedule does not enable replies. Clearing the schedule makes enabled replies immediate
          after saving. Junk filing applies regardless of this schedule.
        </p>
      </fieldset>
      <div>
        <Label htmlFor="reply-subject">Reply subject</Label>
        <Input
          id="reply-subject"
          maxLength={200}
          value={value.subject}
          onChange={(e) => setDraft({ ...value, subject: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor="reply-format">Reply format</Label>
        <Select
          id="reply-format"
          value={value.format}
          onChange={(e) => setDraft({ ...value, format: e.target.value as 'text' | 'html' })}
        >
          <option value="text">Plain text</option>
          <option value="html">HTML</option>
        </Select>
      </div>
      <div>
        <Label htmlFor="reply-message">Reply message</Label>
        <Textarea
          id="reply-message"
          rows={6}
          maxLength={20000}
          required={value.vacationEnabled}
          value={value.message}
          onChange={(e) => setDraft({ ...value, message: e.target.value })}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.junkEnabled}
          onChange={(e) => setDraft({ ...value, junkEnabled: e.target.checked })}
        />
        File classified spam in Junk
      </label>
      <p className="text-sm text-slate-500">
        This controls folder placement. Your provider manages spam classification and antivirus.
        Classified spam receives no vacation reply.
      </p>
      <ErrorNote error={save.error} />
      {save.isSuccess && (
        <p className="text-sm text-emerald-700">Mail preferences saved and verified.</p>
      )}
      <Button
        type="submit"
        disabled={!!(value.startsAt && value.endsAt && value.endsAt <= value.startsAt)}
        loading={save.isPending}
      >
        Save mail preferences
      </Button>
    </form>
  );
}
export function MailPreferencesPanel({
  mailboxId,
  customer,
}: {
  mailboxId: string;
  customer: boolean;
}) {
  const data = useQuery({
    queryKey: ['mail', 'preferences', mailboxId],
    queryFn: () => api.get<MailboxPreferencesDto>(`/api/mail/mailboxes/${mailboxId}/preferences`),
  });
  if (data.isPending) return <Spinner />;
  if (data.error) return <ErrorNote error={data.error} />;
  return (
    <PreferenceForm key={`${mailboxId}:${data.data.state}`} data={data.data} customer={customer} />
  );
}
export function CatchAllForm({
  domain,
  customer,
  onDone,
}: {
  domain: Pick<CustomerMailDomainDto, 'id' | 'catchAllAddress' | 'mailboxes'>;
  customer: boolean;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [address, setAddress] = useState(domain.catchAllAddress ?? '');
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/mail/domains/${domain.id}`, { catchAllAddress: address.trim() || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mail'] });
      onDone();
    },
  });
  const choices = domain.mailboxes.filter((b) => b.deliveryEnabled !== false && !b.orphanedAt);
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <p className="text-sm text-slate-500">
        Choose where mail for unknown addresses is delivered. A catch-all can increase unwanted
        mail. Selecting None removes catch-all forwarding.
      </p>
      <Label htmlFor="catch-all-address">Catch-all mailbox</Label>
      {customer ? (
        <Select id="catch-all-address" value={address} onChange={(e) => setAddress(e.target.value)}>
          <option value="">None</option>
          {address && !choices.some((b) => b.address === address) && (
            <option disabled value={address}>
              Current: {address} — target unavailable
            </option>
          )}
          {choices.map((b) => (
            <option key={b.id} value={b.address}>
              {b.address}
            </option>
          ))}
        </Select>
      ) : (
        <Input
          id="catch-all-address"
          type="email"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Leave empty to disable"
        />
      )}
      <ErrorNote error={save.error} />
      <div className="flex flex-wrap gap-2">
        <Button type="submit" loading={save.isPending}>
          Save catch-all
        </Button>
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
